import { CAT, hydrateCatalogFromCache } from './telemetry-catalog.utils';

export const TELEMETRY_PALETTE = [
  '#CCF54E', '#38BDF8', '#F472B6', '#FACC15',
  '#A78BFA', '#FB923C', '#4ADE80', '#2DD4BF',
  '#E879F9', '#60A5FA', '#F87171', '#C084FC',
];

/**
 * Curated 65 distinct, high-contrast, dark-mode-optimized telemetry series palette.
 * Preserves the original 10 canonical colors for backward compatibility, followed by
 * 55 distinct hues covering all 65 parameters allowed per dashboard panel.
 */
export const TELEMETRY_SERIES_PALETTE: readonly string[] = [
  // 0-9 (Original canonical colors preserved for backward compatibility)
  '#CCF54E', // 0: Bright Lime / Chartreuse
  '#3B82F6', // 1: Electric Blue
  '#F59E0B', // 2: Vibrant Amber
  '#EC4899', // 3: Hot Pink
  '#8B5CF6', // 4: Bright Violet
  '#10B981', // 5: Emerald Green
  '#06B6D4', // 6: Cyan
  '#F43F5E', // 7: Rose Red
  '#A855F7', // 8: Vivid Purple
  '#6366F1', // 9: Indigo Blue

  // 10-19 (High-contrast primary/secondary hues)
  '#14B8A6', // 10: Teal
  '#F97316', // 11: Bright Orange
  '#38BDF8', // 12: Sky Blue
  '#EAB308', // 13: Sunflower Yellow
  '#D946EF', // 14: Fuchsia
  '#84CC16', // 15: Lime Green
  '#0EA5E9', // 16: Ocean Blue
  '#FB7185', // 17: Coral Pink
  '#2DD4BF', // 18: Mint Turquoise
  '#A78BFA', // 19: Soft Purple

  // 20-29 (Warm and cool aerospace tones)
  '#FBBF24', // 20: Warm Gold
  '#22C55E', // 21: Light Green
  '#60A5FA', // 22: Cornflower Blue
  '#F472B6', // 23: Soft Pink
  '#4ADE80', // 24: Spring Green
  '#38E1B0', // 25: Bright Aquamarine
  '#E879F9', // 26: Orchid
  '#FFA07A', // 27: Light Salmon
  '#00E5FF', // 28: Bright Electric Cyan
  '#C084FC', // 29: Light Violet

  // 30-39 (Vibrant pastels & neon accents)
  '#FFB74D', // 30: Apricot
  '#4ECDC4', // 31: Pastel Cyan
  '#FF6B6B', // 32: Coral Red
  '#93C5FD', // 33: Baby Blue
  '#A3E635', // 34: Yellow Lime
  '#F43F85', // 35: Deep Rose
  '#26C6DA', // 36: Turquoise
  '#FF8A65', // 37: Deep Peach
  '#818CF8', // 38: Periwinkle
  '#D4E157', // 39: Pear Green

  // 40-49 (Distinctive jewel and mineral hues)
  '#BA68C8', // 40: Medium Orchid
  '#4DD0E1', // 41: Soft Aqua
  '#FF80AB', // 42: Bright Flamingo
  '#6EE7B7', // 43: Seafoam
  '#FFD54F', // 44: Mustard Gold
  '#7986CB', // 45: Slate Indigo
  '#A5D6A7', // 46: Pale Emerald
  '#FF7043', // 47: Burnt Orange
  '#4DB6AC', // 48: Cadmium Teal
  '#CE93D8', // 49: Thistle Purple

  // 50-64 (Fine aerospace sensor distinct hues)
  '#80CBC4', // 50: Water Blue
  '#FFAB91', // 51: Melon
  '#9FA8DA', // 52: Dusty Lavender
  '#C5E1A5', // 53: Celadon
  '#FF8A80', // 54: Light Coral
  '#80DEEA', // 55: Icy Blue
  '#B39DDB', // 56: Muted Violet
  '#FFF176', // 57: Pale Lemon
  '#81C784', // 58: Fresh Green
  '#FF4081', // 59: Neon Rose
  '#00B0FF', // 60: Vivid Sky
  '#EEFF41', // 61: Electric Yellow
  '#7C4DFF', // 62: Deep Electric Violet
  '#00E676', // 63: Vivid Spring
  '#FF6E40', // 64: Tangerine
];

/**
 * Fast, pure conversion from HSL color values to a hex color string.
 */
function hslToHex(h: number, s: number, l: number): string {
  const hNorm = ((h % 360) + 360) % 360;
  const sNorm = Math.min(100, Math.max(0, s)) / 100;
  const lNorm = Math.min(100, Math.max(0, l)) / 100;

  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const x = c * (1 - Math.abs(((hNorm / 60) % 2) - 1));
  const m = lNorm - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;

  if (hNorm < 60) {
    r = c; g = x; b = 0;
  } else if (hNorm < 120) {
    r = x; g = c; b = 0;
  } else if (hNorm < 180) {
    r = 0; g = c; b = x;
  } else if (hNorm < 240) {
    r = 0; g = x; b = c;
  } else if (hNorm < 300) {
    r = x; g = 0; b = c;
  } else {
    r = c; g = 0; b = x;
  }

  const toHex = (n: number) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

/**
 * Resolves a unique, high-contrast, dark-mode-optimized color for any telemetry series or parameter index.
 * Uses curated TELEMETRY_SERIES_PALETTE (65 distinct colors) and falls back to a deterministic golden-ratio
 * hue generator for indices >= 65, guaranteeing no duplicate colors even with large telemetry sets.
 */
export function getTelemetrySeriesColor(index: number): string {
  const i = Math.max(0, Math.floor(Number(index) || 0));
  if (i < TELEMETRY_SERIES_PALETTE.length) {
    return TELEMETRY_SERIES_PALETTE[i];
  }
  // Golden ratio hue distribution: 137.50776405 degrees per step
  // Lightness alternated (55%-65%) to maximize perceptual contrast against dark backgrounds
  const hue = (i * 137.50776405) % 360;
  const lightness = 55 + (i % 3) * 5;
  return hslToHex(hue, 85, lightness);
}

/** FNV-1a Hash function for consistent color mapping */
export function getTelemetryHashCode(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Resolves a unique, consistent, dark-mode-optimized color for any subsystem.
 * Dynamically indexes subsystems directly from the catalog (CAT) without any hardcoded subsystem assumptions,
 * guaranteeing every subsystem in the current mission receives a distinct color from the 65-color palette.
 * Falls back deterministically to hash distribution across the 65-color palette if not present in the catalog.
 */
export function getTelemetrySubColor(sub: string): string {
  if (!sub) return TELEMETRY_SERIES_PALETTE[0];

  const subTrimmed = String(sub).trim();
  const subLower = subTrimmed.toLowerCase();

  // If catalog is uninitialized in memory, attempt instant cache hydration
  if (Object.keys(CAT).length === 0) {
    hydrateCatalogFromCache();
  }

  // 1. Dynamic Catalog Indexing (Zero hardcoded subsystem assumptions)
  if (CAT && typeof CAT === 'object') {
    const catalogKeys = Object.keys(CAT);
    if (catalogKeys.length > 0) {
      // Find matching subsystem key (case-insensitive)
      let idx = catalogKeys.findIndex((k) => k.toLowerCase() === subLower);

      // Fallback: check if sub matches a subsystem's display label in CAT
      if (idx < 0) {
        idx = catalogKeys.findIndex(
          (k) => CAT[k]?.label && CAT[k].label?.trim().toLowerCase() === subLower
        );
      }

      if (idx >= 0) {
        return getTelemetrySeriesColor(idx);
      }
    }
  }

  // 2. Deterministic Hash Fallback across the 65-color dark-mode palette
  const hash = getTelemetryHashCode(subLower);
  const idx = hash % TELEMETRY_SERIES_PALETTE.length;
  return getTelemetrySeriesColor(idx);
}

/** Human-readable label for telemetry visualization mode */
export function getTelemetryVizLabel(mode: string): string {
  const map: Record<string, string> = {
    line: 'Line Chart',
    area: 'Area Chart',
    kpi: 'KPI Card',
    gauge: 'Gauge',
    radial: 'Radial Dial',
    table: 'Single Table',
    ctable: 'Stream Table',
    cchart: 'Multi-Chart',
    pie: 'Pie Chart',
    bar: 'Bar Chart',
    hbar: 'Horizontal Bar',
    radar: 'Radar Chart',
  };
  return map[mode] || mode;
}

/** Returns true if visualization mode uses ECharts canvas renderer */
export function isTelemetryChartViz(mode: string): boolean {
  if (!mode) return true;
  return ['line', 'area', 'gauge', 'radial', 'cchart', 'multi', 'pie', 'bar', 'hbar', 'radar'].includes(mode);
}


